import { 
  useQuery, 
  useMutation, 
  useQueryClient, 
  UseQueryResult, 
  UseMutationResult 
} from '@tanstack/react-query';
import { 
  createInteractionTemplate, 
  getAllInteractionTemplates, 
  getInteractionsPartRequestFormPartQuantities, 
  getInteractionsPartRequestFormPartTypes, 
  getInteractionsPartRequestFormPartTypesChildrenWithParent, 
  updateInteractionTemplate,
  submitFormsDataWithOriginalPayload,
  getParameterData,
  getServiceSearchData,
  downloadNmiCertificate,
  editInteraction,
  modifyStructuresToggleActive,
  getTriggerPointTypes
} from '../api/interactions';
import {
  InteractionTemplateKey, 
  InteractionsGetParameterFormParameterDataKey, 
  InteractionsPartRequestFormPartQuantitiesKey, 
  InteractionsPartRequestFormPartTypesChildrenWithParentKey,
  InteractionsPartRequestFormPartTypesKey,
  InteractionsServiceSearchFormDataKey,
  TriggerPointKey
} from '../configs/interactions';
import { 
  GetParameterResponseType,
  InteractionsPartRequestFormInitialPartType,
  InteractionsPartRequestFormPartQty,
  InteractionsPartRequestFormPartResponse,
  TemplateListResponse, 
  TriggerPoint, 
  queryOptionType 
} from '../@types/interactions.type';
import { ServiceTypeKey } from '../configs/service-type';
import { 
  CheckInKey, 
  GetServiceKey, 
  ServiceCheckInKey, 
  ServiceLogAllKey 
} from '../configs/services';
import { PendingInteractionKey } from '../configs/pendingInteraction';
import { UploadedFilesKey } from '../configs/uploaded-files';
import { Folders } from '../@types/uploaded-files.type';
import { ServiceObject, ServiceSearchData } from '../@types';

export const useGetAllInteractionTemplates = (queryOptions: queryOptionType): UseQueryResult<TemplateListResponse> => {
  return useQuery({
    queryKey: [InteractionTemplateKey, queryOptions.filterModel],
    queryFn: () => getAllInteractionTemplates(queryOptions),
    enabled: false,
    gcTime: 0
  });
};

export const useAddInteractionTemplate = (): UseMutationResult => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await createInteractionTemplate(data);
      return response;
    },

    onSuccess: async (response: any) => {
      await queryClient.invalidateQueries({
        queryKey: [InteractionTemplateKey, response.id]
      });
      await queryClient.invalidateQueries({
        queryKey: [InteractionTemplateKey]
      });
    }
  });
};

export const useUpdateInteractionTemplate = (id?: number): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => updateInteractionTemplate(data.id, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [InteractionTemplateKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [InteractionTemplateKey, id]
      });
    }
  }); 
};

export const useSubmitFormsDataWithOriginalPayload = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      return await submitFormsDataWithOriginalPayload(data.url, data.method, {
        ...data.payload,
        interactionForms: data.forms || [],
        actionTriggers: data.actionTriggers || []
      });
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceTypeKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceCheckInKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [CheckInKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [PendingInteractionKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [UploadedFilesKey, id, Folders.ServiceAttachments]
      });
    }
  });
};

export const useGetInteractionsPartRequestFormPartTypes = (serviceId: number, conditionCode?: string): UseQueryResult<Array<InteractionsPartRequestFormInitialPartType>> => {
  return useQuery({
    queryKey: [InteractionsPartRequestFormPartTypesKey, serviceId],
    queryFn: () => getInteractionsPartRequestFormPartTypes(serviceId, conditionCode)
  });
};

export const useGetInteractionsPartRequestFormPartTypesChildrenWithParent = (partTypeId: number): UseQueryResult<InteractionsPartRequestFormPartResponse> => {
  return useQuery({
    queryKey: [InteractionsPartRequestFormPartTypesChildrenWithParentKey, partTypeId],
    queryFn: () => partTypeId !== -1 && getInteractionsPartRequestFormPartTypesChildrenWithParent(partTypeId)
  });
};

export const useGetInteractionsPartRequestFormPartQuantities = (partTypeId: number, serviceId: number, serialised: boolean): UseQueryResult<InteractionsPartRequestFormPartQty> => {
  return useQuery({
    queryKey: [InteractionsPartRequestFormPartQuantitiesKey, partTypeId, serviceId],
    queryFn: () => partTypeId !== -1 && getInteractionsPartRequestFormPartQuantities(partTypeId, serviceId, serialised)
  });
};

export const useGetParameterData = (serviceId: number, selectedParameter: string, mainLocationNodeId: number, subLocationNodeId: number, clientId: number): UseQueryResult<GetParameterResponseType> => {
  return useQuery({
    queryKey: [InteractionsGetParameterFormParameterDataKey, serviceId, selectedParameter],
    queryFn: () => selectedParameter && getParameterData(serviceId, selectedParameter, mainLocationNodeId, subLocationNodeId, clientId)
  });
};

export const useGetServiceSearchData = (serviceId: number, serviceSearchData: ServiceSearchData): UseQueryResult<ServiceObject[]> => {
  return useQuery({
    queryKey: [InteractionsServiceSearchFormDataKey, serviceId],
    queryFn: () => serviceSearchData && getServiceSearchData(serviceId,serviceSearchData)
  });
};

export const useDownloadNmiCertificate = (): UseMutationResult<any> => {
  return useMutation({
    mutationFn: async (data: any) => {
      return await downloadNmiCertificate(data);
    }
  });
};

export const useEditInteraction = (): UseMutationResult<any> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await editInteraction(data);
    },

    onSuccess: async (response) => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, response.serviceId]
      });
    }
  });
};

export const useModifyStructuresToggleActive = (): UseMutationResult<any> => {
  return useMutation({
    mutationFn: async (data: any) => {
      return await modifyStructuresToggleActive(data);
    }
  });
};

export const useGetTriggerPointTypes = (): UseQueryResult<TriggerPoint[]> => {
  return useQuery({
    queryKey: [TriggerPointKey],
    queryFn: () => getTriggerPointTypes()
  });
};